import React from 'react'
import { Link } from 'react-router-dom'
import styles from './ScreenErrorBoundary.module.css'

interface State {
  error: Error | null
}

/* One screen failing to render must not take the rail with it: without this,
   an exception anywhere in a page unmounts the whole app and leaves a blank
   window. The shell stays, so the user can go somewhere else. The Shell keys
   this on the path, so navigating away clears it. The handoff specifies no
   wording for this state; see DECISIONS "Shell". */
export class ScreenErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('Screen failed to render:', error, info.componentStack)
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children
    return (
      <div className={styles.wrap} role="alert">
        <h1 className={styles.title}>This screen stopped working</h1>
        <p className={styles.body}>
          Something on this screen failed while it was being drawn. The rest of Control still works. Try again, or
          reload the page if it keeps happening.
        </p>
        <p className={styles.detail}>{error.message || String(error)}</p>
        <div className={styles.actions}>
          <button type="button" className={styles.primary} onClick={() => this.setState({ error: null })}>
            Try again
          </button>
          <button type="button" className={styles.ghost} onClick={() => window.location.reload()}>
            Reload
          </button>
          <Link className={styles.ghost} to="/dashboard">
            Go to Dashboard
          </Link>
        </div>
      </div>
    )
  }
}
